import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import { BellIcon, DroneIcon, GearIcon, UserIcon, VideoCameraIcon } from "@phosphor-icons/react";
import type { AlertEvent, Camera, Drone, PanicButton } from "../api/types";
import { useSession } from "../auth/AuthContext";
import { useSnapshot } from "../cameras/useSnapshot";
import { displayTitle } from "../lib/alertTitle";
import { fmtAgoShort } from "../lib/time";
import { useNow } from "../lib/useNow";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { RadioOrb } from "../radio/RadioOrb";
import { useRealtime } from "../realtime/RealtimeProvider";
import { AlertsPanel, shortTime } from "./AlertsPanel";
import { CameraPanel } from "./CameraPanel";
import { cycleId, tabDirection, type TabFocus } from "./cycle";
import { Hologram, type RadialItem, type RingNode } from "./Hologram";
import { PanicShield } from "./PanicShield";
import { Tether } from "./Tether";
import { LOW_BATTERY_PCT, useSceneData } from "./useSceneData";

// The home scene (approved mockup, DESIGN.md v5): no sidebar. Corners carry
// identity and status, the hologram carries the real things on the site, the
// camera panel floats left, alerts float right, team voice sits bottom center
// and Panic bottom right. Sections open as floating panels over the scene
// (the <Outlet />), so the URL changes and the scene stays put.

const SEL_KEY = "br.cam";

function readSel(): string | null {
  try {
    return window.sessionStorage.getItem(SEL_KEY);
  } catch {
    return null;
  }
}
function writeSel(id: string | null): void {
  try {
    if (id) window.sessionStorage.setItem(SEL_KEY, id);
    else window.sessionStorage.removeItem(SEL_KEY);
  } catch {
    // Storage blocked: selection lasts for this page only.
  }
}

const clockFmt = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

function droneWord(d: Drone): string {
  if (!d.online) return "offline";
  const t = d.telemetry;
  if (t?.armed) return (t.alt_m ?? 0) > 0.5 ? "flying" : "armed";
  return "ready";
}

function droneSub(d: Drone): string {
  const t = d.telemetry;
  const batt = t?.battery_pct != null ? ` · ${Math.round(t.battery_pct)}%` : "";
  if (!d.online) return "offline";
  if (t?.armed) return `${(t.alt_m ?? 0) > 0.5 ? "flying" : "armed"}${batt}`;
  return `landed${batt}`;
}

function buttonNode(b: PanicButton, openPress: AlertEvent | undefined, isOwner: boolean): RingNode {
  const low = b.battery_pct !== null && b.battery_pct < LOW_BATTERY_PCT;
  const tone = openPress ? "hot" : !b.online ? "off" : low ? "warn" : "normal";
  const sub = openPress ? `pressed ${shortTime(openPress.created_at)}` : !b.online ? "offline" : low ? `battery ${b.battery_pct}%` : "ready";
  const action = openPress ? "Open alerts." : isOwner ? "Open button settings." : "Open alerts.";
  return { id: b.id, kind: "btn", name: b.name, sub, tone, label: `${b.name} button, ${sub}. ${action}` };
}

export function HomeScene() {
  const { user, site, isOwner } = useSession();
  const { conn, activeAlerts } = useRealtime();
  const data = useSceneData(isOwner);
  const { cams, buttons, drones, events, blink } = data;
  const location = useLocation();
  const navigate = useNavigate();
  const now = useNow();
  const [menuOpen, setMenuOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(readSel);
  const [liveId, setLiveId] = useState<string | null>(null);
  const sectionOpen = location.pathname !== "/";
  useDocumentTitle(sectionOpen ? null : "Home");

  const camList = useMemo(() => cams.data ?? [], [cams.data]);
  const camIds = useMemo(() => camList.map((c) => c.id), [camList]);
  const camera: Camera | null = camList.find((c) => c.id === selected) ?? camList[0] ?? null;
  const btnList = buttons.data ?? [];
  const droneList = drones.data ?? [];
  const eventList = events.data ?? [];

  const select = useCallback((id: string | null) => {
    setSelected(id);
    writeSel(id);
    setLiveId(null);
  }, []);

  const cycle = useCallback(
    (dir: 1 | -1) => {
      const next = cycleId(camIds, camera?.id ?? null, dir);
      if (next) select(next);
    },
    [camIds, camera, select],
  );

  const open = useCallback((path: string) => navigate(path, { state: { fromScene: true } }), [navigate]);

  // Tab / Shift+Tab switch cameras (see cycle.ts for when Tab is left alone).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ae = document.activeElement;
      const focus: TabFocus = !ae || ae === document.body || ae.classList.contains("scene") ? "idle" : ae.id === "cam-feed" ? "feed" : "other";
      const dir = tabDirection({
        key: e.key,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        altKey: e.altKey,
        focus,
        overlayOpen: sectionOpen || menuOpen || !!document.querySelector("dialog[open]"),
        cameraCount: camIds.length,
      });
      if (!dir) return;
      e.preventDefault();
      cycle(dir);
      if (focus === "idle") document.getElementById("cam-feed")?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sectionOpen, menuOpen, camIds.length, cycle]);

  // Close the radial menu whenever a section opens.
  useEffect(() => {
    if (sectionOpen) setMenuOpen(false);
  }, [sectionOpen]);

  const { snapping, snapshot } = useSnapshot((list) => cams.setData(list));

  // ---- Ring nodes from real data ----
  const openPressFor = (id: string) => eventList.find((e) => e.kind === "button" && e.source_id === id && !e.acked_by);
  const nodes = {
    cam: camList.map<RingNode>((c) => {
      const sub = !c.online ? "offline" : liveId === c.id ? "live" : c.thumbnail_at ? fmtAgoShort(c.thumbnail_at, now) : "no picture";
      return {
        id: c.id,
        kind: "cam",
        name: c.name,
        sub,
        tone: !c.online ? "off" : c.battery === "low" ? "warn" : "normal",
        label: `${c.name} camera, ${sub}. Show in the camera panel.`,
      };
    }),
    btn: btnList.map((b) => buttonNode(b, openPressFor(b.id), isOwner)),
    drone: isOwner
      ? droneList.map<RingNode>((d) => ({
          id: d.id,
          kind: "drone",
          name: d.name,
          sub: droneSub(d),
          tone: !d.online ? "off" : d.telemetry?.armed ? "armed" : "normal",
          label: `${d.name} drone, ${droneSub(d)}. Open drones.`,
        }))
      : [],
  };

  const onNode = (n: RingNode) => {
    if (n.kind === "cam") select(n.id);
    else if (n.kind === "btn") open(n.tone === "hot" || !isOwner ? "/alerts" : "/settings/buttons");
    else open("/drones");
  };

  const radial: RadialItem[] = isOwner
    ? [
        { key: "cameras", label: "cameras", icon: VideoCameraIcon, onSelect: () => open("/cameras") },
        { key: "alerts", label: "alerts", icon: BellIcon, onSelect: () => open("/alerts") },
        { key: "drones", label: "drones", icon: DroneIcon, onSelect: () => open("/drones") },
        { key: "settings", label: "settings", icon: GearIcon, onSelect: () => open("/settings") },
      ]
    : [
        { key: "cameras", label: "cameras", icon: VideoCameraIcon, onSelect: () => open("/cameras") },
        { key: "alerts", label: "alerts", icon: BellIcon, onSelect: () => open("/alerts") },
        { key: "account", label: "account", icon: UserIcon, onSelect: () => open("/account") },
      ];

  // ---- Status line ----
  // Red means an unacknowledged panic the realtime layer is alarming about (same list as the takeover).
  const threat = activeAlerts[0] ?? null;
  const linkDown = conn.state !== "open";
  let status: string;
  if (threat) status = displayTitle(threat.title).toLowerCase();
  else if (!isOwner) status = linkDown ? "reconnecting" : "all clear";
  else {
    const parts = [linkDown ? "reconnecting" : "all clear"];
    if (buttons.data) parts.push(`${btnList.filter((b) => b.online).length} ${btnList.length === 1 ? "button" : "buttons"}`);
    if (droneList.length === 1 && droneList[0]) parts.push(`${droneList[0].name.toLowerCase()} ${droneWord(droneList[0])}`);
    else if (droneList.length > 1) parts.push(`${droneList.filter((d) => d.online).length} drones online`);
    status = parts.join(" · ");
  }

  return (
    <div className={`scene${threat ? " scene--threat" : ""}${sectionOpen ? " scene--section" : ""}`} tabIndex={-1}>
      <header className="corner tl">
        <img src="/brand/mark-64.png" srcSet="/brand/mark-64.png 1x, /brand/mark-180.png 3x" alt="" width={26} height={26} />
        <div>
          <h1 className="org" translate="no">
            {site.name}
          </h1>
          <p className={`statusline${threat ? " statusline--threat" : linkDown ? " statusline--warn" : ""}`} role="status" aria-live="polite">
            <span className="pip" aria-hidden="true" />
            <span>{status}</span>
          </p>
        </div>
      </header>
      <div className="corner tr">
        <Link to={isOwner ? "/settings/password" : "/account"} state={{ fromScene: true }} className="who" aria-label={`${user.name}, ${user.role}. Account and sign out.`}>
          <b>{user.name.toLowerCase()}</b> · {user.role}
        </Link>
        <br />
        <time>{clockFmt.format(now)}</time>
      </div>

      <main className="stage" id="main" aria-label="Home">
        <CameraPanel
          cameras={camList}
          camera={camera}
          blink={blink.data}
          loading={cams.loading}
          error={cams.error}
          isOwner={isOwner}
          live={!!camera && liveId === camera.id}
          setLive={(on) => setLiveId(on && camera ? camera.id : null)}
          snapping={!!(camera && snapping[camera.id])}
          onSnapshot={() => camera && void snapshot(camera)}
          onClips={() => camera && open(`/cameras?clips=${encodeURIComponent(camera.id)}`)}
          onFallbackFull={() => camera && open(`/cameras?live=${encodeURIComponent(camera.id)}`)}
          onCycle={cycle}
          onConnectBlink={() => open("/settings/blink")}
          onLeaveFeed={() => document.querySelector<HTMLElement>(".campanel .actions .btn")?.focus()}
        />
        <Hologram nodes={nodes} selectedId={camera?.id ?? null} onNode={onNode} radial={radial} menuOpen={menuOpen} setMenuOpen={setMenuOpen} threat={!!threat} />
        <Tether selectedId={camera?.id ?? null} layoutKey={`${camList.length}.${btnList.length}.${droneList.length}.${sectionOpen}`} />
        <AlertsPanel events={eventList} error={events.error} loading={events.loading} />
      </main>

      <RadioOrb />
      <PanicShield />

      <Outlet />
    </div>
  );
}
